import { useQuery } from '@tanstack/react-query';
import type { CampaignEditRequest } from '@discovr/types';
import { campaignKeys } from '../queryKeys';
import { createCampaignDetailApi } from '../api/campaignQueries';

const api = createCampaignDetailApi();

/**
 * The campaign's open (not yet fully resolved) edit request, or null if none
 * exists. Shared by both apps: brand's pending-review banner + Comments
 * panel, admin's "Reviewing campaign changes" banner + review panel.
 *
 * Short staleTime (unlike useCampaignHeaderQuery's 10min) since this drives
 * live review-state UI that both parties act on — submit/approve/reject/apply
 * mutations invalidate this key explicitly, but a short staleTime also keeps
 * a second viewer (e.g. admin polling while brand edits) reasonably fresh.
 *
 * `enabled` (default true) mirrors useCampaignHeaderQuery's options shape —
 * CampaignDetailsTab.tsx passes `false` in public mode: there is no authed
 * endpoint an anonymous visitor could call, and edit-request review state
 * has no public-mode rendering path at all.
 */
export function useCampaignEditRequestQuery(campaignId: string, options?: { enabled?: boolean }) {
  return useQuery<CampaignEditRequest | null>({
    queryKey: campaignKeys.editRequestCurrent(campaignId),
    queryFn: () => api.getCurrentEditRequest(campaignId),
    enabled: !!campaignId && (options?.enabled ?? true),
    staleTime: 30_000,
  });
}
